/**
 * The signed-in person's decision right delegations — made to them and made by
 * them (migration 980, roadmap D-4).
 *
 * Read path: my_decision_right_delegations(p_workspace_id), a SECURITY DEFINER
 * readout over decision_right_delegations_v. Whether a delegation is in force,
 * expired or waiting for acceptance, and how many whole days it has left, is
 * computed by the database on read (internal_decision_right_delegation_status),
 * never here, so this page and can_exercise_decision_right cannot disagree.
 * can_extend / can_revoke are the database's answer to "may this person use the
 * control" (build contract §2.8): the page draws Extend and Revoke only on them.
 *
 * Writes are not here: extending is decisionRightsStore.extendDelegation
 * (extend_decision_right_delegation) and revoking is
 * decisionRightsStore.revokeDelegation (revoke_delegation). The words for when
 * a delegation ends are in src/features/home/lib/delegations.ts.
 */

import { getTypedSupabaseClient } from './typedSupabaseClient';
import type { Database } from '../types/database.generated';

type Row = Database['public']['Functions']['my_decision_right_delegations']['Returns'][number];

export type DelegationStatus = 'in_force' | 'expired' | 'awaiting_acceptance' | 'revoked';
export type DelegationDirection = 'to_me' | 'by_me';

export interface MyDelegation {
  id: string;
  direction: DelegationDirection;
  rightId: string;
  workspaceId: string;
  cycleId: string;
  cycleName: string | null;
  domain: string;
  decisionClass: string;
  allowedModes: string[];
  delegateId: string;
  delegateName: string | null;
  delegatedById: string | null;
  delegatedByName: string | null;
  delegatedAt: string;
  expiresAt: string | null;
  status: DelegationStatus;
  inForce: boolean;
  /** Whole days left while in force and dated; null otherwise. */
  daysLeft: number | null;
  canExtend: boolean;
  canRevoke: boolean;
}

const STATUSES: DelegationStatus[] = ['in_force', 'expired', 'awaiting_acceptance', 'revoked'];

export function toMyDelegation(row: Row): MyDelegation {
  const status = (STATUSES as string[]).includes(row.status) ? (row.status as DelegationStatus) : 'in_force';
  return {
    id: row.delegation_id,
    direction: row.direction === 'to_me' ? 'to_me' : 'by_me',
    rightId: row.right_id,
    workspaceId: row.workspace_id,
    cycleId: row.cycle_id,
    cycleName: row.cycle_name ?? null,
    domain: row.domain,
    decisionClass: row.decision_class,
    allowedModes: row.allowed_execution_modes ?? [],
    delegateId: row.delegate_principal_id,
    delegateName: row.delegate_name ?? null,
    delegatedById: row.delegated_by ?? null,
    delegatedByName: row.delegated_by_name ?? null,
    delegatedAt: row.delegated_at,
    expiresAt: row.expires_at ?? null,
    status,
    inForce: Boolean(row.in_force),
    daysLeft: typeof row.days_left === 'number' ? row.days_left : null,
    canExtend: Boolean(row.can_extend),
    canRevoke: Boolean(row.can_revoke),
  };
}

/** The delegations made to and by the signed-in person in one workspace. */
export async function fetchMyDelegations(workspaceId: string | null): Promise<MyDelegation[]> {
  const { data, error } = await getTypedSupabaseClient().rpc(
    'my_decision_right_delegations',
    workspaceId ? { p_workspace_id: workspaceId } : {},
  );
  if (error) throw new Error(error.message);
  return (data ?? []).map(toMyDelegation);
}

// ── The end-date input (the Decision Rights matrix and the home both set one) ──

/** A date input's value (YYYY-MM-DD, local time) as the end of that day, ISO. Null when it is not a date. */
export function endOfDayIso(dateInput: string): string | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dateInput.trim());
  if (!m) return null;
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]), 23, 59, 59, 0);
  if (d.getFullYear() !== Number(m[1]) || d.getMonth() !== Number(m[2]) - 1 || d.getDate() !== Number(m[3])) return null;
  return d.toISOString();
}

/** Where the Extend form's date starts: a week past the later of the current end and today (YYYY-MM-DD, local). */
export function suggestedEndDate(expiresAt: string | null, now: Date = new Date()): string {
  const end = expiresAt ? new Date(expiresAt) : null;
  const base = end && end.getTime() > now.getTime() ? end : now;
  const next = new Date(base.getFullYear(), base.getMonth(), base.getDate() + 7);
  const mm = String(next.getMonth() + 1).padStart(2, '0');
  const dd = String(next.getDate()).padStart(2, '0');
  return `${next.getFullYear()}-${mm}-${dd}`;
}
